import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaBuilding, FaChevronLeft, FaCogs, FaQuestionCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import B2BHubHomeHeader from '../../components/B2BHubHomeHeader';
import HubFloatingRail from '../../components/hub/HubFloatingRail';
import { CategoryBrowseGrid, buildSearchParams, readParam } from '../../components/marketplace/MarketplaceBrowseUi';
import { resolveHubImage } from '../../utils/b2bHubHomeMedia';
import { useMasterFacilityTypes } from '../../hooks/useMasterFacilityTypes';
import { useMasterFacilitySubTypes } from '../../hooks/useMasterFacilitySubTypes';
import { useMasterFacilitySubSubTypes } from '../../hooks/useMasterFacilitySubSubTypes';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/home.scss';
import '../../assets/styles/product_marketplace.scss';

const LISTING_TYPE_ITEMS = [
  {
    id: 'property',
    name: 'Property',
    subtitle: 'Office, warehouse, factory, land',
    imagePath: '/images/infrastructure-leasing/listing-types/property.png',
  },
  {
    id: 'machinery',
    name: 'Machinery / Asset',
    subtitle: 'Equipment, machinery, infrastructure',
    imagePath: '/images/infrastructure-leasing/listing-types/machinery.png',
  },
];

function mapFacilityType(row) {
  return {
    id: row.facility_type_id,
    name: row.facility_type,
    imagePath: row.image_path || null,
  };
}

function mapFacilitySubType(row) {
  return {
    id: row.facility_sub_type_id,
    name: row.facility_sub_type,
    imagePath: row.image_path || null,
  };
}

function mapFacilitySubSubType(row) {
  return {
    id: row.facility_sub_sub_type_id,
    name: row.facility_sub_sub_type,
    imagePath: row.image_path || null,
  };
}

function getBrowseLevel(applied) {
  const { type, assetCategory, subCategory, subSubCategory } = applied;
  if (!type) return 0;
  if (!assetCategory) return 1;
  if (!subCategory) return 2;
  if (type === 'machinery' && !subSubCategory) return 3;
  return -1;
}

function listingsHref(params) {
  const next = new URLSearchParams();
  if (params.type) next.set('type', params.type);
  if (params.assetCategory) next.set('assetCategory', params.assetCategory);
  if (params.subCategory) next.set('subCategory', params.subCategory);
  if (params.subSubCategory) next.set('subSubCategory', params.subSubCategory);
  const qs = next.toString();
  return qs ? `/infrastructure-leasing/listings?${qs}` : '/infrastructure-leasing/listings';
}

function browseHref(params) {
  const next = new URLSearchParams();
  if (params.type) next.set('type', params.type);
  if (params.assetCategory) next.set('assetCategory', params.assetCategory);
  if (params.subCategory) next.set('subCategory', params.subCategory);
  if (params.subSubCategory) next.set('subSubCategory', params.subSubCategory);
  const qs = next.toString();
  return qs ? `/infrastructure-leasing/browse?${qs}` : '/infrastructure-leasing/browse';
}

function ListingTypePickGrid({ onSelect }) {
  return (
    <section className="product-marketplace-browse">
      <div className="product-marketplace-browse__intro">
        <h2>Browse by Listing Type</h2>
        <p>Select property or machinery to explore asset categories.</p>
      </div>
      <div className="product-marketplace-browse__grid product-marketplace-browse__grid--2">
        {LISTING_TYPE_ITEMS.map((item) => (
          <button
            key={item.id}
            type="button"
            className="product-marketplace-browse__item product-marketplace-browse__item--type"
            onClick={() => onSelect(item.id)}
          >
            {item.imagePath ? (
              <span className="product-marketplace-tabs__icon product-marketplace-tabs__icon--image">
                <img src={resolveHubImage(item.imagePath)} alt={item.name} loading="lazy" className="product-marketplace-tabs__img" />
              </span>
            ) : (
              <span className="product-marketplace-tabs__icon product-marketplace-tabs__icon--fallback">
                {item.id === 'property' ? <FaBuilding aria-hidden /> : <FaCogs aria-hidden />}
              </span>
            )}
            <span className="product-marketplace-tabs__label">{item.name}</span>
            <span className="product-marketplace-browse__type-sub">{item.subtitle}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

export default function InfrastructureLeasingBrowsePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const applied = useMemo(
    () => ({
      type: readParam(searchParams, 'type'),
      assetCategory: readParam(searchParams, 'assetCategory'),
      subCategory: readParam(searchParams, 'subCategory'),
      subSubCategory: readParam(searchParams, 'subSubCategory'),
    }),
    [searchParams],
  );

  const browseLevel = useMemo(() => getBrowseLevel(applied), [applied]);
  const listingType = applied.type === 'property' || applied.type === 'machinery' ? applied.type : null;

  const { list: facilityTypes, loading: loadingTypes } = useMasterFacilityTypes(listingType);
  const { list: facilitySubTypes, loading: loadingSubTypes } = useMasterFacilitySubTypes(applied.assetCategory);
  const { list: facilitySubSubTypes, loading: loadingSubSubTypes } = useMasterFacilitySubSubTypes(applied.subCategory);

  const assetCategoryItems = useMemo(() => facilityTypes.map(mapFacilityType), [facilityTypes]);
  const subCategoryItems = useMemo(() => facilitySubTypes.map(mapFacilitySubType), [facilitySubTypes]);
  const subSubCategoryItems = useMemo(() => facilitySubSubTypes.map(mapFacilitySubSubType), [facilitySubSubTypes]);

  const activeAssetCategory = assetCategoryItems.find((c) => String(c.id) === String(applied.assetCategory));
  const activeSubCategory = subCategoryItems.find((c) => String(c.id) === String(applied.subCategory));
  const activeSubSubCategory = subSubCategoryItems.find((c) => String(c.id) === String(applied.subSubCategory));

  useEffect(() => {
    if (browseLevel !== -1) return;
    navigate(listingsHref(applied), { replace: true });
  }, [browseLevel, applied, navigate]);

  const patchParams = (updates) => {
    setSearchParams(buildSearchParams(searchParams, updates));
  };

  const goBack = () => {
    if (browseLevel === 3) {
      patchParams({ subCategory: '', subSubCategory: '' });
      return;
    }
    if (browseLevel === 2) {
      patchParams({ subCategory: '', subSubCategory: '' });
      return;
    }
    if (browseLevel === 1) {
      patchParams({ assetCategory: '', subCategory: '', subSubCategory: '' });
      return;
    }
    if (browseLevel === 0) {
      patchParams({ type: '', assetCategory: '', subCategory: '', subSubCategory: '' });
    }
  };

  const handleBrowseSelect = (id) => {
    if (browseLevel === 0) {
      patchParams({ type: String(id), assetCategory: '', subCategory: '', subSubCategory: '' });
      return;
    }
    if (browseLevel === 1) {
      patchParams({ assetCategory: String(id), subCategory: '', subSubCategory: '' });
      return;
    }
    if (browseLevel === 2) {
      if (applied.type === 'property') {
        navigate(listingsHref({ ...applied, subCategory: String(id) }));
        return;
      }
      patchParams({ subCategory: String(id), subSubCategory: '' });
      return;
    }
    if (browseLevel === 3) {
      navigate(listingsHref({ ...applied, subSubCategory: String(id) }));
    }
  };

  const browseCopy = useMemo(() => {
    if (browseLevel === 0) {
      return null;
    }
    if (browseLevel === 1) {
      return {
        title: listingType === 'machinery' ? 'Machinery / Asset Categories' : 'Property Asset Categories',
        subtitle: 'Choose an asset category to continue.',
        items: assetCategoryItems,
        loading: loadingTypes,
        emptyMessage: 'No asset categories available for this listing type.',
      };
    }
    if (browseLevel === 2) {
      return {
        title: activeAssetCategory?.name || 'Sub Categories',
        subtitle:
          applied.type === 'property'
            ? 'Choose a sub-category to view listings.'
            : 'Choose a sub-category to explore further.',
        items: subCategoryItems,
        loading: loadingSubTypes,
        emptyMessage: 'No sub-categories found for this asset category.',
      };
    }
    return {
      title: activeSubCategory?.name || 'Sub-Sub Categories',
      subtitle: 'Choose a sub-sub-category to view listings.',
      items: subSubCategoryItems,
      loading: loadingSubSubTypes,
      emptyMessage: 'No sub-sub-categories found for this sub-category.',
    };
  }, [
    browseLevel,
    listingType,
    assetCategoryItems,
    loadingTypes,
    activeAssetCategory,
    applied.type,
    subCategoryItems,
    loadingSubTypes,
    activeSubCategory,
    subSubCategoryItems,
    loadingSubSubTypes,
  ]);

  const typeLabel = applied.type === 'machinery' ? 'Machinery / Asset' : applied.type === 'property' ? 'Property' : null;

  if (browseLevel === -1) {
    return null;
  }

  return (
    <div className="product-marketplace-page">
      <B2BHubHomeHeader />

      <main className="product-marketplace-main">
        <nav className="product-marketplace-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span className="product-marketplace-breadcrumb__sep">&gt;</span>
          {browseLevel === 0 ? (
            <span className="product-marketplace-breadcrumb__current">Infrastructure Leasing</span>
          ) : (
            <Link to="/infrastructure-leasing/browse">Infrastructure Leasing</Link>
          )}
          {typeLabel ? (
            <>
              <span className="product-marketplace-breadcrumb__sep">&gt;</span>
              {browseLevel > 1 ? (
                <Link to={browseHref({ type: applied.type })}>{typeLabel}</Link>
              ) : (
                <span className="product-marketplace-breadcrumb__current">{typeLabel}</span>
              )}
            </>
          ) : null}
          {applied.assetCategory ? (
            <>
              <span className="product-marketplace-breadcrumb__sep">&gt;</span>
              {browseLevel > 2 ? (
                <Link to={browseHref({ type: applied.type, assetCategory: applied.assetCategory })}>
                  {activeAssetCategory?.name || 'Asset Category'}
                </Link>
              ) : (
                <span className="product-marketplace-breadcrumb__current">
                  {activeAssetCategory?.name || 'Asset Category'}
                </span>
              )}
            </>
          ) : null}
          {applied.subCategory ? (
            <>
              <span className="product-marketplace-breadcrumb__sep">&gt;</span>
              {browseLevel > 3 ? (
                <Link
                  to={browseHref({
                    type: applied.type,
                    assetCategory: applied.assetCategory,
                    subCategory: applied.subCategory,
                  })}
                >
                  {activeSubCategory?.name || 'Sub Category'}
                </Link>
              ) : (
                <span className="product-marketplace-breadcrumb__current">
                  {activeSubCategory?.name || 'Sub Category'}
                </span>
              )}
            </>
          ) : null}
        </nav>

        <div className="product-marketplace-heading">
          <div className="product-marketplace-heading__text">
            <h1>Infrastructure Leasing</h1>
            <p>Browse property and machinery listings step by step.</p>
          </div>
          <div className="product-marketplace-heading__actions">
            {browseLevel > 0 ? (
              <button type="button" className="product-marketplace-back" onClick={goBack}>
                <FaChevronLeft aria-hidden /> Back
              </button>
            ) : null}
            <button
              type="button"
              className="product-marketplace-how"
              onClick={() =>
                toast.info(
                  'Select property or machinery, then asset category and sub-categories to view filtered listings.',
                )
              }
            >
              <FaQuestionCircle aria-hidden /> How it works
            </button>
          </div>
        </div>

        {browseLevel === 0 ? (
          <ListingTypePickGrid onSelect={handleBrowseSelect} />
        ) : (
          <CategoryBrowseGrid
            title={browseCopy.title}
            subtitle={browseCopy.subtitle}
            items={browseCopy.items}
            loading={browseCopy.loading}
            emptyMessage={browseCopy.emptyMessage}
            onSelect={handleBrowseSelect}
          />
        )}
      </main>
      <HubFloatingRail />
    </div>
  );
}
